/**
 * SchemaDetail Component
 *
 * Right panel with tabs for different metadata aspects.
 */

import React from 'react'
import { Tabs, Typography, Spin, Empty, Alert, Space, Tag, Tooltip } from 'antd'
import {
  InfoCircleOutlined,
  TableOutlined,
  ApiOutlined,
  RetweetOutlined,
  CodeOutlined,
  DatabaseOutlined
} from '@ant-design/icons'
import { OverviewTab, FieldsTab, ApiTab, LifecycleTab, RawJsonTab } from './tabs'
import { getIconFromName } from './utils'
import type { SchemaDetailProps } from './types'

const { Title, Text } = Typography

export function SchemaDetail({
  entity,
  metadata,
  isLoading,
  error
}: SchemaDetailProps): React.ReactElement {
  // No entity selected
  if (!entity) {
    return (
      <div style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fafafa'
      }}>
        <Empty
          image={<DatabaseOutlined style={{ fontSize: '64px', color: '#d9d9d9' }} />}
          description={
            <Space direction="vertical" size={4}>
              <Text type="secondary">Select an entity from the list</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                to view its metadata details
              </Text>
            </Space>
          }
        />
      </div>
    )
  }

  // Derive display name from entity
  const displayName = entity.name
  const entityPath = entity.path

  // Loading state
  if (isLoading) {
    return (
      <div style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Space direction="vertical" align="center">
          <Spin size="large" />
          <Text type="secondary">Loading {displayName} metadata...</Text>
        </Space>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          type="error"
          showIcon
          message={`Failed to load metadata for ${displayName}`}
          description={
            <Space direction="vertical">
              <Text type="secondary">{error}</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                The entity might not have a metadata endpoint at {entityPath}/metadata.
              </Text>
            </Space>
          }
        />
      </div>
    )
  }

  // No metadata returned
  if (!metadata) {
    return (
      <div style={{ padding: '24px' }}>
        <Empty description={`No metadata available for ${displayName}`} />
      </div>
    )
  }

  // Calculate stats for header
  const fieldCount = Object.keys(metadata.fields || {}).length
  const hasLifecycle = metadata.lifecycle?.enabled
  const hasApi = metadata.api?.endpoints && Object.keys(metadata.api.endpoints).length > 0

  // Tab items - pass entityPath for consistency with API terminology
  const tabItems = [
    {
      key: 'overview',
      label: (
        <Space>
          <InfoCircleOutlined />
          <span>Overview</span>
        </Space>
      ),
      children: <OverviewTab metadata={metadata} schemaName={displayName} />
    },
    {
      key: 'fields',
      label: (
        <Space>
          <TableOutlined />
          <span>Fields</span>
          <Tag style={{ marginLeft: '4px' }}>{fieldCount}</Tag>
        </Space>
      ),
      children: <FieldsTab metadata={metadata} schemaName={displayName} />
    },
    {
      key: 'api',
      label: (
        <Space>
          <ApiOutlined />
          <span>API</span>
          {hasApi && (
            <Tag color="green" style={{ marginLeft: '4px' }}>
              {Object.values(metadata.api!.endpoints).filter(Boolean).length}
            </Tag>
          )}
        </Space>
      ),
      children: <ApiTab metadata={metadata} schemaName={displayName} />
    },
    {
      key: 'lifecycle',
      label: (
        <Space>
          <RetweetOutlined />
          <span>Lifecycle</span>
          {hasLifecycle && (
            <Tag color="blue" style={{ marginLeft: '4px' }}>
              {Object.keys(metadata.lifecycle!.actions).length}
            </Tag>
          )}
        </Space>
      ),
      children: <LifecycleTab metadata={metadata} schemaName={displayName} />
    },
    {
      key: 'raw',
      label: (
        <Space>
          <CodeOutlined />
          <span>Raw JSON</span>
        </Space>
      ),
      children: <RawJsonTab metadata={metadata} schemaName={displayName} />
    }
  ]

  return (
    <div style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 24px',
        borderBottom: '1px solid #f0f0f0',
        background: '#fff'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            {/* Icon */}
            <Tooltip title={metadata.display?.icon || 'Default icon'}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                background: '#f0f5ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #d6e4ff',
                fontSize: '20px',
                color: '#1890ff'
              }}>
                {getIconFromName(metadata.display?.icon)}
              </div>
            </Tooltip>
            <div>
              <Title level={4} style={{ margin: 0 }}>
                {metadata.display?.label_singular || displayName}
              </Title>
              <Space style={{ marginTop: '4px' }} size={8}>
                <Text code style={{ fontSize: '12px', color: '#1890ff' }}>
                  {entityPath}
                </Text>
                {metadata.api?.path && metadata.api.path !== entityPath && (
                  <Text code style={{ fontSize: '12px' }}>
                    API: {metadata.api.path}
                  </Text>
                )}
              </Space>
            </div>
          </div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {fieldCount} fields
          </Text>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <Tabs
          defaultActiveKey="overview"
          items={tabItems}
          style={{ height: '100%' }}
          tabBarStyle={{
            margin: 0,
            padding: '0 24px',
            background: '#fff'
          }}
          tabBarGutter={24}
        />
      </div>
    </div>
  )
}

export default SchemaDetail
